import {
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardCopyIcon,
} from '@radix-ui/react-icons';
import {
  Button,
  Card,
  Code,
  DataList,
  Flex,
  IconButton,
  Popover,
  Spinner,
} from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { MachineContext } from 'contexts/machine.context';
import { parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import { t } from 'i18next';
import {
  LOCAL_DATETIME_FORMAT,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IDataCollection } from 'lib_ts/interfaces/i-data-collection';
import { ICollectionShotGroup } from 'lib_ts/interfaces/training/i-machine-shot';
import { sumBy } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { DataCollectionsService as DCService } from 'services/data-collections.service';
import { useHardwareDiagnosticStore } from './store/use-hardware-diagnostic-store';

interface IProps {
  hwd: IDataCollection;
}

export const HardwareDiagnosticDetails = (props: IProps) => {
  const { machine } = useContext(MachineContext);
  const [openDetails, setOpenDetails] = useState(false);
  const [agg, setAgg] = useState<ICollectionShotGroup[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!openDetails) {
      return;
    }

    setLoading(true);

    DCService.getShotSummaries(props.hwd._id)
      .then((result) => setAgg(result))
      .finally(() => setLoading(false));

    setLoading(false);
  }, [props.hwd, openDetails, machine]);

  return (
    <ErrorBoundary identifier="HardwareDiagnosticDetails">
      <Popover.Root onOpenChange={(open) => setOpenDetails(open)}>
        <Popover.Trigger>
          <Button
            size={RADIX.BUTTON.SIZE.MODAL}
            color={RADIX.COLOR.SECONDARY}
            variant="soft"
          >
            {t('common.details')}
            {openDetails ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </Button>
        </Popover.Trigger>
        <Popover.Content asChild>
          <Card style={{ minWidth: '200px' }}>
            <Flex
              p="1" // +3 (from card) = 4
              direction="column"
              gap="2"
            >
              <DataList.Root orientation="vertical" size="1">
                <DataList.Item>
                  <DataList.Label>{t('common.ball-type')}</DataList.Label>
                  <DataList.Value>{props.hwd.ball_type}</DataList.Value>
                </DataList.Item>
                <DataList.Item>
                  <DataList.Label>{t('Started On')}</DataList.Label>
                  <DataList.Value>
                    {format(
                      parseISO(props.hwd._created),
                      LOCAL_DATETIME_FORMAT,
                      {
                        timeZone: LOCAL_TIMEZONE,
                      }
                    )}
                  </DataList.Value>
                </DataList.Item>

                <DataList.Item>
                  <DataList.Label>{t('common.diagnostic-id')}</DataList.Label>
                  <DataList.Value>
                    <Flex
                      gap="2"
                      justify="between"
                      align="center"
                      style={{ width: '100%' }}
                    >
                      <Code color={RADIX.COLOR.NEUTRAL}>
                        {props.hwd.collectionID}
                      </Code>

                      <IconButton
                        className="btn-floating"
                        color={RADIX.COLOR.NEUTRAL}
                        size="1"
                        variant="soft"
                        onClick={() => {
                          if (!props.hwd) {
                            return;
                          }

                          navigator.clipboard.writeText(props.hwd.collectionID);

                          NotifyHelper.success({
                            message_md: t('common.x-copied-to-clipboard', {
                              x: props.hwd.collectionID,
                            }),
                          });
                        }}
                      >
                        <ClipboardCopyIcon />
                      </IconButton>
                    </Flex>
                  </DataList.Value>
                </DataList.Item>

                <DataList.Item>
                  <DataList.Label>
                    {t('common.diagnostic-shots')}
                  </DataList.Label>
                  <DataList.Value>
                    {loading ? <Spinner /> : sumBy(agg, (m) => m.total)}
                  </DataList.Value>
                </DataList.Item>
              </DataList.Root>

              {!props.hwd.complete && (
                <Button
                  size="1"
                  variant={RADIX.BUTTON.VARIANT.SECONDARY}
                  color={RADIX.COLOR.DANGER}
                  onClick={() =>
                    useHardwareDiagnosticStore.getState().tryCancel()
                  }
                >
                  Cancel
                </Button>
              )}
            </Flex>
          </Card>
        </Popover.Content>
      </Popover.Root>
    </ErrorBoundary>
  );
};
